import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ordersApi } from "../services/orders";
import type { TopDJ } from "../types/orders";
import { rankClass, rowClass, rowIndex } from "./DJLeaderboard";
import LeaderboardFlame from "./LeaderboardFlame";
import "./DJLeaderboard.css";
import { absMediaUrl } from "../utils/mediaUrl";
import { t, formatLocale } from "../i18n";
import { DEFAULT_AVATAR_SRC, showDefaultAvatar } from "../lib/defaultAvatar";
import { useAccountCache } from "../hooks/useAccountCache";

// Platform-wide Top DJs — the second view on the leaderboard page pill
// toggle. Ranked by accepted request count (never money, matching the
// /trending privacy stance). Tapping a row opens that DJ's public fan
// request page.

const joinedLabel = (iso: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return t("topDJsBoard.joined", { date: d.toLocaleDateString(formatLocale(), { month: "short", year: "numeric" }) });
};

const TopDJsBoard: React.FC = () => {
  const navigate = useNavigate();
  // Kept for the session (hooks/useAccountCache): switching views or reopening
  // the board paints the last list at once instead of a skeleton and photos
  // loading again (founder, b47: "a kind of glitchy thing").
  const [djs, setDjs] = useAccountCache<TopDJ[] | null>("topdjs", null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    ordersApi
      // The public twin of /orders/top-djs (same handler server-side): the
      // fan app carries no DJ token, and the DJ app works against it too.
      .topDjsPublic()
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.djs) ? res.djs : [];
        setDjs(
          rows
            .filter((d: any) => d && typeof d.slug === "string" && typeof d.name === "string")
            // Top 3 only while the platform is young (founder decision) — a
            // 10-deep board with thin data reads as emptiness, not glory.
            .slice(0, 3)
            .map((d: any) => ({
              name: d.name,
              slug: d.slug,
              avatarUrl: typeof d.avatarUrl === "string" && d.avatarUrl ? d.avatarUrl : null,
              joined: typeof d.joined === "string" ? d.joined : null,
              uniqueFans: Number(d.uniqueFans ?? d.accepted) || 0,
            }))
        );
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [setDjs]);

  if (failed) return <div className="djlb-empty">{t("topDJsBoard.couldnTLoadTop")}</div>;

  return (
    <div className="djlb-cards">
      <div className="djlb-card">
        <div className="djlb-card-title"><LeaderboardFlame size={20} />{t("topDJsBoard.topTalent")}</div>
        {!djs ? (
          <div className="djlb-skel" aria-hidden="true">
            {Array.from({ length: 3 }).map((_, i) => (
              <div className="djlb-skel-row" key={i} />
            ))}
          </div>
        ) : djs.length === 0 ? (
          <div className="djlb-empty">{t("topDJsBoard.noAcceptedRequestsYet")}</div>
        ) : (
          <ol className="djlb-list">
            {djs.map((d, i) => {
              const avatar = absMediaUrl(d.avatarUrl);
              const joined = joinedLabel(d.joined);
              return (
                <li className={rowClass(i)} key={d.slug} style={rowIndex(i)}>
                  <button
                    type="button"
                    className="djlb-rowbtn"
                    onClick={() => navigate(`/dj/${d.slug}/request`)}
                    aria-label={t("topDJsBoard.openSRequestPage", { name: d.name })}
                  >
                    <span className={rankClass(i)}>{i + 1}</span>
                    {/* No photo (or one that fails): the drawn default picture. */}
                    <img
                      className="djlb-art djlb-art--round"
                      src={avatar || DEFAULT_AVATAR_SRC}
                      alt=""
                      onError={showDefaultAvatar}
                    />
                    <span className="djlb-meta">
                      <span className="djlb-name">{d.name}</span>
                      {joined && <span className="djlb-sub">{joined}</span>}
                    </span>
                    <span className="djlb-count">{i === 0 && <LeaderboardFlame size={17} live />}{d.uniqueFans}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
};

export default TopDJsBoard;
